import {
  CheckCircle,
  Circle,
  MinusCircle,
  PlusCircle,
} from "@phosphor-icons/react";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { BusinessMembership, CapabilityExperience } from "./types";
import { WorkspaceSetupDetail } from "../workspace/WorkspaceSetupDetail";

export function CapabilityInstallationPage({
  business,
  embedded = false,
  onSignOut,
}: {
  business: BusinessMembership;
  embedded?: boolean;
  onSignOut: () => void;
}) {
  const businessId = business.businessId as Id<"businesses">;
  const experience = useQuery(
    api.businessConfiguration.getInstallationExperience,
    { businessId },
  ) as CapabilityExperience | undefined;

  const panel = (
    <CapabilityPanel businessId={businessId} experience={experience} />
  );

  if (embedded) {
    return (
      <WorkspaceSetupDetail
        description="Enable or disable existing capabilities within their server-validated dependencies."
        eyebrow="Capabilities"
        title="Business capabilities"
      >
        {panel}
      </WorkspaceSetupDetail>
    );
  }

  return (
    <main className="foundation-shell business-setup-shell">
      <section aria-live="polite" className="status-panel">
        <span className="status-label">Capability experience</span>
        <strong>{business.name}</strong>
        <span>{business.role} workspace</span>
        <button onClick={onSignOut} type="button">
          Sign out
        </button>
      </section>
      {panel}
    </main>
  );
}

function CapabilityPanel({
  businessId,
  experience,
}: {
  businessId: Id<"businesses">;
  experience: CapabilityExperience | undefined;
}) {
  const enableCapability = useMutation(
    api.businessConfiguration.enableCapability,
  );
  const disableCapability = useMutation(
    api.businessConfiguration.disableCapability,
  );
  const [feedback, setFeedback] = useState("Ready");
  const [pendingCapability, setPendingCapability] = useState<string | null>(
    null,
  );
  const owner = experience?.role === "OWNER";

  async function changeCapability(
    capability: CapabilityExperience["capabilities"][number]["capability"],
    action: "enable" | "disable",
  ) {
    if (!owner || pendingCapability) return;
    setPendingCapability(capability);
    setFeedback(action === "enable" ? "Enabling" : "Disabling");
    try {
      if (action === "enable") {
        await enableCapability({ businessId, capability });
      } else {
        await disableCapability({ businessId, capability });
      }
      setFeedback(action === "enable" ? "Enabled" : "Disabled");
    } catch {
      setFeedback("Change denied");
    } finally {
      setPendingCapability(null);
    }
  }

  return (
    <section className="business-setup-panel capability-panel">
      <div className="business-setup-toolbar">
        <div>
          <span className="status-label">Profile</span>
          <strong>{experience?.profile ?? "Not configured"}</strong>
        </div>
        <div>
          <span className="status-label">Readiness</span>
          <strong>{experience?.readinessStatus ?? feedback}</strong>
        </div>
      </div>
      <div className="capability-grid">
        {(experience?.capabilities ?? []).map((item) => (
          <article className="capability-card" key={item.capability}>
            <header>
              {item.enabled ? (
                <CheckCircle aria-hidden weight="fill" />
              ) : item.installed ? (
                <MinusCircle aria-hidden weight="fill" />
              ) : (
                <Circle aria-hidden />
              )}
              <div>
                <strong>{item.capability}</strong>
                <span>{item.recommended ? "Recommended" : "Available"}</span>
              </div>
            </header>
            <p>{readinessLabel(item)}</p>
            <div className="capability-actions">
              <button
                disabled={!owner || item.enabled || Boolean(pendingCapability)}
                onClick={() => void changeCapability(item.capability, "enable")}
                type="button"
              >
                <PlusCircle aria-hidden weight="bold" />
                Enable
              </button>
              <button
                disabled={!owner || !item.enabled || Boolean(pendingCapability)}
                onClick={() =>
                  void changeCapability(item.capability, "disable")
                }
                type="button"
              >
                <MinusCircle aria-hidden weight="bold" />
                Disable
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function readinessLabel(item: CapabilityExperience["capabilities"][number]) {
  if (item.readiness.status === "READY") return "Dependencies ready";
  return `Needs ${item.readiness.missingDependencies.join(", ")}`;
}
